import { useContext, useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { Outlet, useParams } from 'react-router-dom';

import { BreadcrumbProvider } from '../../contexts/breadcrumb';
import {
  GameDocumentContext,
  GameDocumentProvider
} from '../../contexts/game-document';
import { deleteGameFromDB } from '../../utils/game-document/indexedDB';
import { resetDatastateSession } from '../../utils/game-document/session-storage';
import DraftRecoveryDialog from '../../components/dialogs/draft-recovery-dialog';

export interface MapConfigStore {
  zoneId?: string;
  activePanelBar?:
    | 'settings'
    | 'contents'
    | 'behaviours'
    | 'design'
    | 'details';
  selectedMoveContent?: 'ALL' | 'TASK' | 'ZONE' | 'AREA' | 'IMAGE';
  selectedAddContent?: 'Task' | 'Zone' | 'Area' | 'Image' | 'Annotation';
  showAllTasks?: boolean;
  showTaskTitle?: boolean;
  contentSearch?: string;
  mapContentTasks?: boolean;
  mapContentImages?: boolean;
  mapContentAreas?: boolean;
  mapContentZones?: boolean;
  mapContentAnnotations?: boolean;
}

const GameDesignerContent = () => {
  const [state, setState] = useContext(GameDocumentContext);

  const handleRecover = () => {
    if (state.pendingRecovery) {
      setState({
        ...state.pendingRecovery,
        shouldRecoverDraft: false,
        pendingRecovery: undefined
      });
    }
  };

  const handleDiscard = async () => {
    if (state.gameId) {
      await deleteGameFromDB(state.gameId);
      // After deleting, we need to load from server.
      // We can just refresh or call a reload function.
      // For now, let's just trigger a reload of the document from server.
      setState((prev) => ({
        ...prev,
        shouldRecoverDraft: false,
        pendingRecovery: undefined,
        isLoaded: false
      }));
      // Reload the page.
      window.location.reload();
    }
  };

  return (
    <>
      {state.shouldRecoverDraft && (
        <DraftRecoveryDialog
          onRecover={handleRecover}
          onDiscard={handleDiscard}
        />
      )}
      <BreadcrumbProvider>
        <Outlet />
      </BreadcrumbProvider>
    </>
  );
};

const GameDesigner = () => {
  let { gameId } = useParams();
  const [, setMapConfig, removeMapConfig] = useLocalStorage<MapConfigStore>(
    `map-config-${gameId}`,
    {
      zoneId: '',
      activePanelBar: 'settings',
      selectedMoveContent: 'ALL',
      selectedAddContent: 'Task',
      showAllTasks: false,
      showTaskTitle: true,
      contentSearch: '',
      mapContentTasks: false,
      mapContentImages: false,
      mapContentAreas: false,
      mapContentZones: false,
      mapContentAnnotations: false
    }
  );

  useEffect(() => {
    setMapConfig((prev) => ({ ...prev, activePanelBar: 'settings' }));

    return () => {
      //remove session storage datastate grid assets in game designer
      resetDatastateSession();
      //remove stored map config
      removeMapConfig();
    };
  }, []);

  // prettier-ignore
  return (
    <GameDocumentProvider gameId={gameId!}>
      <GameDesignerContent />
    </GameDocumentProvider>
  );
};

export default GameDesigner;
